import { CardElement, CardElementProps, useElements, useStripe } from '@stripe/react-stripe-js';
import styles from './ConnectStripe.module.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { StripeCardElementChangeEvent, StripeElements } from '@stripe/stripe-js';
import {
    placeHolderAxiosGuest,
    placeHolderAxiosGuestCustomer,
    setPaymentMethodAxios,
    setPaymentMethodAxiosCustomer
} from './StripeServices';

import { toastAlert } from '~/utils/toastify';
import { get } from 'lodash';
import { CartAtom } from '~/services/cart';
import OrderAtom from '~/services/order/order.atom';
import { useRouter } from 'next/router';
import { UserAddress, UserAtom } from '~/services/user';
import userAtom from '~/services/user/user.atom';
import { toast } from 'react-toastify';
import { PaymentMethods } from '~/services/payment';
import { PATH } from '~/constants/enum';
import React from 'react';
import * as Sentry from '@sentry/nextjs';
import { useApolloClient } from '@apollo/client';
import { GET_CART_INFO_GQL } from '~/data/cart/gql';

type Props = {} & CardElementProps;

// Checkout errors are only shown as toasts, so report them to Sentry (with replay) to know who hit them
const reportCheckoutError = (
    stage: 'create_payment_method' | 'set_payment_method' | 'place_order' | 'stripe_next_action',
    cart: any,
    detail: { message?: string; code?: string; status?: number; error?: any }
) => {
    // Stripe client secrets (pi_xxx_secret_xxx) can appear in backend messages
    const message =
        typeof detail.message === 'string'
            ? detail.message.replace(/(pi|seti)_[A-Za-z0-9]+_secret_[A-Za-z0-9]+/g, '[redacted]').slice(0, 300)
            : undefined;
    Sentry.withScope((scope) => {
        scope.setLevel('error');
        scope.setTag('checkout.stage', stage);
        if (detail.code) scope.setTag('checkout.error_code', detail.code);
        if (detail.status) scope.setTag('checkout.http_status', String(detail.status));
        // Same user identity AppLayout already sets, so the event links to its replay
        if (cart?.email) scope.setUser({ email: cart.email });
        scope.setContext('checkout', {
            cart_id: cart?.cartId,
            total: cart?.prices?.grand_total?.value ?? cart?.total,
            message,
            code: detail.code,
            status: detail.status
        });
        scope.setFingerprint(['checkout', stage, detail.code || String(detail.status || 'unknown')]);
        if (detail.error instanceof Error) {
            Sentry.captureException(detail.error);
        } else {
            Sentry.captureMessage(`Checkout ${stage} failed: ${message || detail.code || 'unknown'}`);
        }
    });
};

export const ContentElementStrip = (props: Props) => {
    const elements: any = useElements();
    const stripe = useStripe();
    const setCart = useSetRecoilState(CartAtom.cart);
    const cart = useRecoilValue(CartAtom.cartSelector);
    const currentUser = useRecoilValue(userAtom.currentUser);
    const setOrder = useSetRecoilState(OrderAtom.order);
    // const [error, setError] = useState<any>();
    //   const [isSubmitting, setIsSumiting] = useState<any>();
    const [cardComplete, setCardComplete] = useState(false);
    const router = useRouter();
    const apolloClient = useApolloClient();

    // The order can be created even when the place-order request fails on the client (timeout, dropped
    // connection, 5xx after commit). Magento then deactivates the cart, so before showing an error check
    // whether the cart was converted, otherwise the shopper retries and gets charged twice.
    const isCartConverted = async (cartId: string | undefined, attempts: number) => {
        if (!cartId) return false;
        for (let attempt = 0; attempt < attempts; attempt++) {
            if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 2000));
            try {
                // 'no-cache': the client runs with ssrMode, which silently turns 'network-only' into 'cache-first'
                await apolloClient.query({
                    query: GET_CART_INFO_GQL,
                    variables: { cartId },
                    fetchPolicy: 'no-cache'
                });
            } catch (error: any) {
                if (String(error?.message || '').includes("isn't active")) return true;
                return false;
            }
        }
        return false;
    };

    // Returns true when the order turned out to be placed and the shopper was sent to the thank-you page
    const recoverPlacedOrder = async (stage: 'set_payment_method' | 'place_order') => {
        // place_order: the order may still be committing, so re-check a few times
        if (!(await isCartConverted(cart.cartId as string, stage === 'place_order' ? 5 : 1))) return false;
        reportCheckoutError(stage, cart, { code: 'order_placed_despite_error' });
        handleOnSuccess({ order_number: '' }, cart);
        return true;
    };

    const retrievePaymentIntent = useCallback(
        async (response: any) => {
            const message = response?.data?.message;
            const reportPlaceOrderError = () =>
                reportCheckoutError('place_order', cart, {
                    message: typeof message === 'string' ? message : response?.message,
                    status: response?.status ?? response?.response?.status,
                    error: response instanceof Error ? response : undefined
                });
            if (!message) {
                reportPlaceOrderError();
                if (await recoverPlacedOrder('place_order')) return;
                toast.error(typeof message === 'string' ? message : 'Something went wrong. Please try again!');

                // setProcessing(false);
                setCart((prevCart) => ({
                    ...prevCart,
                    statusPlaceOrder: 'failed'
                }));

                return;
            }
            if (message?.includes('Authentication Required: ')) {
                const paymentIntent = message.substring('Authentication Required: '.length)?.split(',')[0];

                return stripe
                    ?.handleNextAction({
                        clientSecret: paymentIntent
                    })
                    .then((result) => {
                        if (result.error) {
                            reportCheckoutError('stripe_next_action', cart, {
                                message: result.error.message,
                                code: result.error.code || result.error.decline_code
                            });
                            toast.error(result.error.message);
                            setCart((prevCart) => ({
                                ...prevCart,
                                statusPlaceOrder: 'failed'
                            }));
                        } else if (result.paymentIntent) {
                            if (result.paymentIntent.status === 'succeeded') {
                                handlePlaceHolder({ cartId: cart.cartId as string });
                            } else if (result.paymentIntent.status === 'requires_confirmation') {
                                return stripe.confirmCardPayment(paymentIntent).then((confirmResult) => {
                                    if (confirmResult.error) {
                                        toast.error(confirmResult.error.message);
                                        setCart((prevCart) => ({
                                            ...prevCart,
                                            statusPlaceOrder: 'failed'
                                        }));
                                    } else {
                                        handlePlaceHolder({ cartId: cart.cartId as string });
                                    }
                                });
                            } else {
                                toast.error('Payment failed. Please try again.');
                                setCart((prevCart) => ({
                                    ...prevCart,
                                    statusPlaceOrder: 'failed'
                                }));
                            }
                        } else {
                            toast.error('An unexpected error occurred. Please try again.');
                            setCart((prevCart) => ({
                                ...prevCart,
                                statusPlaceOrder: 'failed'
                            }));
                        }
                    })
                    .catch((error) => {
                        console.error('Stripe handleNextAction error:', error);
                        toast.error('An error occurred while processing your payment. Please try again.');
                        setCart((prevCart) => ({
                            ...prevCart,
                            statusPlaceOrder: 'failed'
                        }));
                    });
                // return stripe?.retrievePaymentIntent(paymentIntent).then((result) => {
                //   // setProcessing(false);
                //   if (result.error) {
                //     toast.error(result?.error?.message);
                //   }
                //   if (result?.paymentIntent?.status == "requires_action") {
                //     if (result.paymentIntent.confirmation_method == "manual") {
                //       stripe?.handleCardAction(paymentIntent).then(function (result) {
                //         if (result.error) {
                //           toastAlert({ type: "error", message });
                //           setCart((prevCart) => ({
                //             ...prevCart,
                //             statusPlaceOrder: "failed",
                //           }));
                //         } else {
                //           // setProcessing(true);
                //           handlePlaceHolder({ cartId: cart.cartId as string });
                //         }
                //       });
                //     }
                //   } else {
                //     setCart((prevCart) => ({
                //       ...prevCart,
                //       statusPlaceOrder: "failed",
                //     }));
                //   }
                // });
            }
            // setProcessing(false);
            reportPlaceOrderError();
            if (await recoverPlacedOrder('place_order')) return;
            toast.error(message);
            setCart((prevCart) => ({
                ...prevCart,
                statusPlaceOrder: 'failed'
            }));
        },
        [cart, stripe]
    );

    const handleOnSuccess = (placeholderResponse: any, cart: any) => {
        toast.success('Congratulation, your order is completed');

        setCart((prevCart) => ({
            ...prevCart,
            statusPlaceOrder: 'success',
            selected_payment_method: {
                title: 'Pay by Card',
                code: PaymentMethods.STRIPE
            }
        }));
        setOrder({
            ...cart,
            order_number: placeholderResponse.order_number,
            selected_payment_method: {
                title: 'Pay by Card',
                code: PaymentMethods.STRIPE
            },
            referer: PATH.Checkout
        });
        router.push('/thank-you');
    };

    const handlePlaceHolder = async ({ cartId }: any) => {
        if (currentUser?.token) {
            return await placeHolderAxiosGuestCustomer({ cartId }, localStorage.getItem('token') as string).then(
                (response) => {
                    if (response.status !== 200) {
                        retrievePaymentIntent(response);
                    } else {
                        handleOnSuccess(get(response, 'data[0]'), cart);
                    }
                }
            );
        }

        return await placeHolderAxiosGuest({ cartId }).then((response) => {
            if (response.status != 200) {
                retrievePaymentIntent(response);
            } else {
                handleOnSuccess(get(response, 'data[0]'), cart);
            }
        });
    };

    const handleSubmit = async () => {
        // setIsSumiting(true)
        if (!stripe || !elements) {
            // Stripe.js has not loaded yet. Make sure to disable
            // form submission until Stripe.js has loaded.
            return;
        }

        // if (cardComplete) {
        //     setProcessing(true);
        // }]

        // Postal code input is hidden in CardElement, so pass the checkout address ZIP to Stripe instead
        const address = cart?.billing_address?.postcode ? cart.billing_address : cart?.shipping_addresses?.[0];
        const postalCode = address?.postcode?.trim();
        const payload = await stripe.createPaymentMethod({
            type: 'card',
            card: elements.getElement(CardElement),
            billing_details: {
                email: cart?.email,
                phone: cart?.billing_address?.telephone,
                name: `${cart?.billing_address?.firstname} ${cart?.billing_address?.lastname}`,
                ...(postalCode && {
                    address: {
                        postal_code: postalCode,
                        country: address?.country?.code || address?.country_code || undefined
                    }
                })
            }
        });
        // setProcessing(false);
        if (payload.error) {
            // setError();
            reportCheckoutError('create_payment_method', cart, {
                message: payload.error.message,
                code: payload.error.code || payload.error.decline_code
            });
            toast.error(payload?.error.message);
            elements?.getElement('card').focus();
            setCart((prevCart) => ({
                ...prevCart,
                statusPlaceOrder: 'failed'
            }));
            // setProcessing(false);
        } else {
            try {
                const paymentMethod = payload.paymentMethod;
                const resSetPayment = !!localStorage.getItem('token')
                    ? await setPaymentMethodAxiosCustomer(
                          {
                              cartId: cart.cartId as string,
                              cc_stripejs_token: paymentMethod.id
                          },
                          localStorage.getItem('token') as string
                      )
                    : await setPaymentMethodAxios({
                          cartId: cart.cartId as string,
                          cc_stripejs_token: paymentMethod.id
                      });
                if (resSetPayment.status == 200) {
                    handlePlaceHolder({
                        cartId: cart.cartId as string
                    });
                } else {
                    reportCheckoutError('set_payment_method', cart, {
                        message: resSetPayment?.data?.message,
                        status: resSetPayment?.status
                    });
                    if (await recoverPlacedOrder('set_payment_method')) return;
                    toast.error(resSetPayment?.data?.message || 'Something went wrong. Please try again!');
                    setCart((prevCart) => ({
                        ...prevCart,
                        statusPlaceOrder: 'failed'
                    }));

                    // setProcessing(false);
                }
            } catch (error: any) {
                reportCheckoutError('set_payment_method', cart, {
                    message: error?.response?.data?.message || error?.message,
                    status: error?.response?.status,
                    error
                });
                if (await recoverPlacedOrder('set_payment_method')) return;
                toast.error(error?.message || 'Something went wrong. Please try again!');
                setCart((prevCart) => ({
                    ...prevCart,
                    statusPlaceOrder: 'failed'
                }));
            }
        }
        // setIsSumiting(false)
    };

    const handleChangeStripeInput = useCallback((event: StripeCardElementChangeEvent) => {
        // if (!!event.error) {
        setCardComplete(event.complete);
        // setError(event?.error?.message || '');
        props?.onChange?.(event);
        // }
    }, []);

    useEffect(() => {
        if (cart?.statusPlaceOrder === 'processing' && cardComplete) {
            handleSubmit();
        } else if (cart?.statusPlaceOrder === 'processing' && !cardComplete) {
            setCart((prevCart) => ({
                ...prevCart,
                statusPlaceOrder: 'failed'
            }));
        }
    }, [cart?.statusPlaceOrder]);

    return (
        <React.Fragment>
            <div className={styles.input_container}>
                <CardElement
                    className={styles.input_field}
                    {...props}
                    onChange={handleChangeStripeInput}
                    options={{
                        disableLink: true,
                        hidePostalCode: true
                    }}
                />
            </div>
            {/* {<span className={"text-error"}>{error}</span>} */}
        </React.Fragment>
    );
};
